import { Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import React, { useMemo } from "react";
import { useRecoilValue } from "recoil";

import PriceModal from "components/Common/PriceModal/PriceModal";
import useCartHooks from "components/hooks/Cart/useCartHooks";
import { EvalIds, OrderStatus, PlatformType } from "graphql/generated";
import { platformState } from "src/states";
import { CheckoutTypes } from "src/types/checkout";
import { checkIsConsultOnly } from "src/utils";

import WarningReNewModal from "../WarningRenewModal";
import { checkIsTeleConsultProduct } from "../utils";
import { RemoveCartItemModal } from "./Modal";
import OTCItem from "./OTCItem";
import { OTCItemTeleConsultFee } from "./OTCItemTeleConsultFee";
import PrescriptionItem from "./PrescriptionItem";
import SummarySection from "./SummarySection";

type Props = CheckoutTypes & {
  t: (key: string, values?: any) => string;
  allowAction?: boolean;
  shouldShowCashback?: boolean;
};

// THANH TODO Refactor hook

const PricesSection = ({ t, summary, items, allowAction, shouldShowCashback }: Props) => {
  const platform = useRecoilValue(platformState);

  const {
    onRemoveItemClick,
    onOpenCancelModal,
    onTogglePriceClick,
    onQuantityChange,
    onToggleAutoRenew,
    onEditPriceClick, // Change in Edit ticket
    cart,
    hasUnpaidTextConsult
  } = useCartHooks();

  const isRenewal = !!cart?.cartItems?.find((item) => {
    return (
      !isEmpty(item?.oldOrder) &&
      item?.oldOrder?.status !== OrderStatus.PrescriptionRejected &&
      item?.product?.metadata?.requires_prescription
    );
  });

  const isAllConsultOnly = useMemo(
    () =>
      items.every((i) => {
        const { product } = i;
        const evalId = product.evaluationId?.[platform || PlatformType.Noah] as EvalIds;

        return checkIsConsultOnly(evalId, product.uniqName);
      }),
    [items, platform]
  );

  return items.length > 0 ? (
    <Flex direction="column">
      <Flex direction="column" overflow="auto">
        {items.map((item: any) => {
          const { product } = item;
          const { requires_prescription: requiresPrescription } = product.metadata;

          if (checkIsTeleConsultProduct(product)) {
            return (
              <OTCItemTeleConsultFee
                key={item.sysId}
                platform={platform}
                item={item}
                onOpenCancelModal={onOpenCancelModal}
                allowAction={allowAction}
                summary={summary}
              />
            );
          }

          if (!requiresPrescription) {
            return <React.Fragment key={product.sysId} />;
          }

          return (
            <PrescriptionItem
              t={t}
              item={item}
              onOpenCancelModal={onOpenCancelModal}
              onToggleAutoRenew={onToggleAutoRenew}
              onTogglePriceClick={onTogglePriceClick}
              onEditPriceClick={onEditPriceClick}
              allowAction={allowAction}
              summary={summary}
            />
          );
        })}
      </Flex>

      <Flex direction="column" overflow="auto">
        {items.map((item: any) => {
          const { product } = item;
          const { requires_prescription: requiresPrescription } = product.metadata;

          if (checkIsTeleConsultProduct(product)) {
            return <React.Fragment key={product.sysId} />;
          }

          return (
            !requiresPrescription && (
              <OTCItem
                t={t}
                item={item}
                onOpenCancelModal={onOpenCancelModal}
                onTogglePriceClick={onTogglePriceClick}
                onToggleAutoRenew={onToggleAutoRenew}
                onQuantityChange={onQuantityChange}
                onEditPriceClick={onEditPriceClick}
                allowAction={allowAction}
                summary={summary}
              />
            )
          );
        })}
      </Flex>

      <SummarySection
        t={t}
        summary={summary}
        isRenewal={isRenewal}
        hideShipping={isAllConsultOnly}
        hasUnpaidTextConsult={hasUnpaidTextConsult}
        shouldShowCashback={shouldShowCashback}
      />

      <RemoveCartItemModal onConfirmClick={onRemoveItemClick} />

      <WarningReNewModal t={t} />

      {allowAction && <PriceModal isSuggestion />}
    </Flex>
  ) : null;
};

export default PricesSection;
